import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  DollarSign, TrendingUp, Eye, MousePointer, Plus, 
  LogOut, CreditCard, BarChart3, Image, Play, Upload
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Auth helpers
const setAdvertiserToken = (token) => localStorage.setItem('advertiser_token', token);
const getAdvertiserToken = () => localStorage.getItem('advertiser_token');
const clearAdvertiserAuth = () => {
  localStorage.removeItem('advertiser_token');
  localStorage.removeItem('advertiser_data');
};
const setAdvertiserData = (data) => localStorage.setItem('advertiser_data', JSON.stringify(data));
const getAdvertiserData = () => {
  const data = localStorage.getItem('advertiser_data');
  return data ? JSON.parse(data) : null;
};

// API helper with auth
const advertiserApi = axios.create({ baseURL: API });
advertiserApi.interceptors.request.use((config) => {
  const token = getAdvertiserToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Login/Register Component
const AdvertiserAuth = ({ onLogin }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    company_name: '',
    contact_name: '',
    phone: '',
    website: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const endpoint = isLogin ? '/advertiser/login' : '/advertiser/register';
      const payload = isLogin 
        ? { email: formData.email, password: formData.password }
        : formData;
      
      const res = await axios.post(`${API}${endpoint}`, payload);
      setAdvertiserToken(res.data.access_token);
      setAdvertiserData(res.data.advertiser);
      onLogin(res.data.advertiser);
    } catch (err) {
      setError(err.response?.data?.detail || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white text-center">
            {isLogin ? 'Advertiser Login' : 'Create Advertiser Account'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <>
                <div>
                  <Label className="text-gray-300">Company Name *</Label>
                  <Input
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    className="bg-gray-700 border-gray-600 text-white"
                    required
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Contact Name *</Label>
                  <Input
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    className="bg-gray-700 border-gray-600 text-white"
                    required
                  />
                </div>
              </>
            )}
            <div>
              <Label className="text-gray-300">Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="bg-gray-700 border-gray-600 text-white"
                required
              />
            </div>
            <div>
              <Label className="text-gray-300">Password *</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="bg-gray-700 border-gray-600 text-white"
                required
              />
            </div>
            {!isLogin && (
              <div>
                <Label className="text-gray-300">Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
            )}
            {error && <p className="text-red-400 text-sm">{error}</p>}
            <Button type="submit" className="w-full bg-sky-500 hover:bg-sky-600" disabled={loading}>
              {loading ? 'Please wait...' : (isLogin ? 'Login' : 'Create Account')}
            </Button>
          </form>
          <p className="text-center text-gray-400 mt-4">
            {isLogin ? "Don't have an account? " : "Already have an account? "}
            <button 
              onClick={() => setIsLogin(!isLogin)} 
              className="text-sky-400 hover:underline"
            >
              {isLogin ? 'Sign up' : 'Login'}
            </button>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

// Dashboard Component
const AdvertiserDashboard = ({ advertiser, onLogout }) => {
  const [searchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [showAddFunds, setShowAddFunds] = useState(false);
  const [showNewCampaign, setShowNewCampaign] = useState(false);
  const [balance, setBalance] = useState(advertiser?.balance || 0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
    
    // Check for payment callback
    const sessionId = searchParams.get('session_id');
    const paymentStatus = searchParams.get('payment');
    if (sessionId && paymentStatus === 'success') {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const loadData = async () => {
    try {
      const [statsRes, campaignsRes, profileRes] = await Promise.all([
        advertiserApi.get('/advertiser/stats'),
        advertiserApi.get('/advertiser/campaigns'),
        advertiserApi.get('/advertiser/me')
      ]);
      setStats(statsRes.data);
      setCampaigns(campaignsRes.data);
      setBalance(profileRes.data.balance);
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) return;
    
    try {
      const res = await advertiserApi.get(`/advertiser/checkout/status/${sessionId}`);
      if (res.data.payment_status === 'paid') {
        loadData();
        alert('Payment successful! Your balance has been updated.');
      } else if (res.data.status !== 'expired') {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (err) {
      console.error('Error checking payment:', err);
    }
  };

  const handleAddFunds = async (packageId) => {
    try {
      const res = await advertiserApi.post(`/advertiser/checkout?package_id=${packageId}&origin_url=${encodeURIComponent(window.location.origin)}`);
      window.location.href = res.data.url;
    } catch (err) {
      console.error('Checkout error:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Unknown error';
      alert('Error creating checkout: ' + errorMsg);
    }
  };

  const handleActivateCampaign = async (campaignId) => {
    try {
      await advertiserApi.put(`/advertiser/campaigns/${campaignId}/activate`);
      loadData();
      alert('Campaign activated!');
    } catch (err) {
      alert(err.response?.data?.detail || 'Error activating campaign');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <p className="text-white">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Advertiser Dashboard</h1>
          <p className="text-gray-400">{advertiser?.company_name}</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="bg-green-500/20 text-green-400 px-4 py-2 rounded-lg font-semibold">
            Balance: ${balance.toFixed(2)}
          </div>
          <Button onClick={() => setShowAddFunds(true)} className="bg-sky-500 hover:bg-sky-600">
            <CreditCard className="w-4 h-4 mr-2" /> Add Funds
          </Button>
          <Button variant="ghost" onClick={onLogout} className="text-gray-400">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <BarChart3 className="w-8 h-8 text-sky-400" />
              <div>
                <p className="text-gray-400 text-sm">Active Campaigns</p>
                <p className="text-2xl font-bold text-white">{stats?.active_campaigns || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Eye className="w-8 h-8 text-purple-400" />
              <div>
                <p className="text-gray-400 text-sm">Total Impressions</p>
                <p className="text-2xl font-bold text-white">{stats?.total_impressions?.toLocaleString() || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <MousePointer className="w-8 h-8 text-green-400" />
              <div>
                <p className="text-gray-400 text-sm">Total Clicks</p>
                <p className="text-2xl font-bold text-white">{stats?.total_clicks?.toLocaleString() || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <DollarSign className="w-8 h-8 text-yellow-400" />
              <div>
                <p className="text-gray-400 text-sm">Total Spent</p>
                <p className="text-2xl font-bold text-white">${stats?.total_spent?.toFixed(2) || '0.00'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Campaigns Section */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-white">Campaigns</h2>
          <Button onClick={() => setShowNewCampaign(true)} className="bg-sky-500 hover:bg-sky-600">
            <Plus className="w-4 h-4 mr-2" /> New Campaign
          </Button>
        </div>
        
        {campaigns.length === 0 ? (
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="py-12 text-center">
              <p className="text-gray-400">No campaigns yet. Create your first campaign to start advertising!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {campaigns.map(campaign => (
              <Card key={campaign.id} className="bg-gray-800 border-gray-700">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-white font-semibold">{campaign.name}</h3>
                      <p className="text-gray-400 text-sm">
                        Budget: ${campaign.budget} | Spent: ${campaign.spent?.toFixed(2) || '0.00'}
                      </p>
                      <div className="flex gap-2 mt-2">
                        {campaign.placements?.map(p => (
                          <Badge key={p} className="bg-sky-500/20 text-sky-300">{p}</Badge>
                        ))}
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge className={
                        campaign.status === 'active' ? 'bg-green-500' :
                        campaign.status === 'draft' ? 'bg-yellow-500' :
                        campaign.status === 'paused' ? 'bg-orange-500' : 'bg-gray-500'
                      }>
                        {campaign.status}
                      </Badge>
                      {campaign.status === 'draft' && (
                        <Button 
                          size="sm" 
                          onClick={() => handleActivateCampaign(campaign.id)}
                          className="mt-2 bg-green-500 hover:bg-green-600"
                        >
                          Activate
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Add Funds Dialog */}
      <Dialog open={showAddFunds} onOpenChange={setShowAddFunds}>
        <DialogContent className="bg-gray-800 border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-white">Add Funds to Account</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 mt-4">
            {[
              { id: 'starter', amount: 50, label: 'Starter' },
              { id: 'growth', amount: 100, label: 'Growth' },
              { id: 'professional', amount: 250, label: 'Professional' },
              { id: 'enterprise', amount: 500, label: 'Enterprise' }
            ].map(pkg => (
              <button
                key={pkg.id}
                onClick={() => handleAddFunds(pkg.id)}
                className="p-4 bg-gray-700 hover:bg-gray-600 rounded-lg text-center transition-colors"
              >
                <p className="text-2xl font-bold text-white">${pkg.amount}</p>
                <p className="text-gray-400 text-sm">{pkg.label}</p>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* New Campaign Dialog */}
      <NewCampaignDialog 
        open={showNewCampaign} 
        onClose={() => setShowNewCampaign(false)}
        onCreated={() => { setShowNewCampaign(false); loadData(); }}
      />
    </div>
  );
};

// New Campaign Dialog
const NewCampaignDialog = ({ open, onClose, onCreated }) => {
  const [step, setStep] = useState(1); // 1: Campaign Details, 2: Ad Creative
  const [formData, setFormData] = useState({
    name: '',
    budget: 50,
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    placements: []
  });
  const [adCreative, setAdCreative] = useState({
    name: '',
    ad_type: 'display',
    image_url: '',
    video_url: '',
    click_url: '',
    duration_seconds: 30
  });
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);

  const placements = [
    { id: 'news', label: 'News Articles', icon: <Image className="w-4 h-4" />, category: 'Website' },
    { id: 'podcast', label: 'Podcast Page', icon: <Play className="w-4 h-4" />, category: 'Website' },
    { id: 'radio', label: 'Radio Page', icon: <Play className="w-4 h-4" />, category: 'Website' },
    { id: 'video_player', label: 'Video Player (Pre-roll)', icon: <Play className="w-4 h-4" />, category: 'Website' },
    { id: 'stream_overlay', label: 'Stream Overlay', icon: <Image className="w-4 h-4" />, category: 'Website' },
    { id: 'social_youtube', label: 'YouTube (16:9)', icon: <Play className="w-4 h-4" />, category: 'Social Media' },
    { id: 'social_youtube_shorts', label: 'YouTube Shorts (9:16)', icon: <Play className="w-4 h-4" />, category: 'Social Media' },
    { id: 'social_instagram_reels', label: 'Instagram Reels (9:16)', icon: <Play className="w-4 h-4" />, category: 'Social Media' },
    { id: 'social_tiktok', label: 'TikTok (9:16)', icon: <Play className="w-4 h-4" />, category: 'Social Media' },
  ];

  const togglePlacement = (id) => {
    setFormData(prev => ({
      ...prev,
      placements: prev.placements.includes(id)
        ? prev.placements.filter(p => p !== id)
        : [...prev.placements, id]
    }));
  };

  const hasVideoPlacement = formData.placements.some(p => p.includes('video_'));

  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    formDataUpload.append('media_type', type);

    try {
      const res = await advertiserApi.post('/advertiser/upload-media', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (type === 'video') {
        setAdCreative(prev => ({ ...prev, video_url: res.data.url }));
      } else {
        setAdCreative(prev => ({ ...prev, image_url: res.data.url }));
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Error uploading file');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (step === 1) {
      if (formData.placements.length === 0) {
        alert('Please select at least one placement');
        return;
      }
      setStep(2);
      setAdCreative(prev => ({ ...prev, name: formData.name + ' - Ad' }));
      return;
    }

    // Step 2: Create campaign and ad
    if (!adCreative.image_url && !adCreative.video_url) {
      alert('Please upload an image or video for your ad');
      return;
    }

    setLoading(true);
    try {
      // Create campaign first
      const campaignRes = await advertiserApi.post('/advertiser/campaigns', {
        ...formData,
        start_date: new Date(formData.start_date).toISOString(),
        end_date: new Date(formData.end_date).toISOString()
      });

      // Then create the ad
      await advertiserApi.post('/advertiser/ads', {
        campaign_id: campaignRes.data.id,
        name: adCreative.name,
        ad_type: adCreative.video_url ? 'video' : 'display',
        image_url: adCreative.image_url || null,
        video_url: adCreative.video_url || null,
        click_url: adCreative.click_url || null,
        duration_seconds: adCreative.duration_seconds
      });

      onCreated();
      // Reset form
      setStep(1);
      setFormData({
        name: '',
        budget: 50,
        start_date: new Date().toISOString().split('T')[0],
        end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        placements: []
      });
      setAdCreative({
        name: '',
        ad_type: 'display',
        image_url: '',
        video_url: '',
        click_url: '',
        duration_seconds: 30
      });
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      const errorMsg = typeof errorDetail === 'object' 
        ? JSON.stringify(errorDetail) 
        : (errorDetail || err.message || 'Error creating campaign');
      alert(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-gray-800 border-gray-700 max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white">
            {step === 1 ? 'Create New Campaign' : 'Upload Ad Creative'}
          </DialogTitle>
          <div className="flex gap-2 mt-2">
            <div className={`flex-1 h-1 rounded ${step >= 1 ? 'bg-sky-500' : 'bg-gray-600'}`} />
            <div className={`flex-1 h-1 rounded ${step >= 2 ? 'bg-sky-500' : 'bg-gray-600'}`} />
          </div>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {step === 1 ? (
            <>
              <div>
                <Label className="text-gray-300">Campaign Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-gray-700 border-gray-600 text-white"
                  required
                />
              </div>
              <div>
                <Label className="text-gray-300">Budget (min $50) *</Label>
                <Input
                  type="number"
                  min="50"
                  value={formData.budget}
                  onChange={(e) => setFormData({ ...formData, budget: parseFloat(e.target.value) })}
                  className="bg-gray-700 border-gray-600 text-white"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Start Date *</Label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="bg-gray-700 border-gray-600 text-white"
                    required
                  />
                </div>
                <div>
                  <Label className="text-gray-300">End Date *</Label>
                  <Input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="bg-gray-700 border-gray-600 text-white"
                    required
                  />
                </div>
              </div>
              <div>
                <Label className="text-gray-300">Ad Placements *</Label>
                
                {/* Website Placements */}
                <p className="text-gray-500 text-xs mt-3 mb-2">Website</p>
                <div className="grid grid-cols-2 gap-2">
                  {placements.filter(p => p.category === 'Website').map(p => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => togglePlacement(p.id)}
                      className={`p-3 rounded-lg flex items-center gap-2 transition-colors ${
                        formData.placements.includes(p.id)
                          ? 'bg-sky-500 text-white'
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                    >
                      {p.icon}
                      <span className="text-sm">{p.label}</span>
                    </button>
                  ))}
                </div>
                
                {/* Social Media Placements */}
                <p className="text-gray-500 text-xs mt-4 mb-2">Social Media (Logo on Highlights)</p>
                <div className="grid grid-cols-2 gap-2">
                  {placements.filter(p => p.category === 'Social Media').map(p => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => togglePlacement(p.id)}
                      className={`p-3 rounded-lg flex items-center gap-2 transition-colors ${
                        formData.placements.includes(p.id)
                          ? 'bg-purple-500 text-white'
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                    >
                      {p.icon}
                      <span className="text-sm">{p.label}</span>
                    </button>
                  ))}
                </div>

              </div>
            </>
          ) : (
            <>
              <div>
                <Label className="text-gray-300">Ad Name</Label>
                <Input
                  value={adCreative.name}
                  onChange={(e) => setAdCreative({ ...adCreative, name: e.target.value })}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>

              {/* Image Upload */}
              <div>
                <Label className="text-gray-300">Display Image {!hasVideoPlacement && '*'}</Label>
                <p className="text-gray-500 text-xs mb-2">Upload a banner image (JPG, PNG, max 10MB)</p>
                <div className="flex gap-2">
                  <Input
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={(e) => handleFileUpload(e, 'image')}
                    className="bg-gray-700 border-gray-600 text-white"
                    disabled={uploading}
                  />
                </div>
                {adCreative.image_url && (
                  <div className="mt-2 p-2 bg-gray-700 rounded">
                    <img src={adCreative.image_url} alt="Preview" className="max-h-32 rounded" />
                    <p className="text-xs text-green-400 mt-1">✓ Image uploaded</p>
                  </div>
                )}
              </div>

              {/* Video Upload - Only show if video placement selected */}
              {hasVideoPlacement && (
                <div>
                  <Label className="text-gray-300">Video Commercial *</Label>
                  <p className="text-gray-500 text-xs mb-2">Upload a video file (MP4, WebM, max 100MB)</p>
                  <div className="flex gap-2">
                    <Input
                      type="file"
                      accept="video/mp4,video/webm,video/quicktime"
                      onChange={(e) => handleFileUpload(e, 'video')}
                      className="bg-gray-700 border-gray-600 text-white"
                      disabled={uploading}
                    />
                  </div>
                  {adCreative.video_url && (
                    <div className="mt-2 p-2 bg-gray-700 rounded">
                      <video 
                        src={adCreative.video_url} 
                        className="max-h-32 rounded w-full" 
                        controls 
                        playsInline
                        preload="metadata"
                      />
                      <p className="text-xs text-green-400 mt-1">✓ Video uploaded successfully</p>
                      <p className="text-xs text-gray-400 truncate">{adCreative.video_url}</p>
                    </div>
                  )}
                  <div className="mt-2">
                    <Label className="text-gray-300 text-sm">Video Duration</Label>
                    <select
                      value={adCreative.duration_seconds}
                      onChange={(e) => setAdCreative({ ...adCreative, duration_seconds: parseInt(e.target.value) })}
                      className="w-full bg-gray-700 border border-gray-600 text-white rounded-md p-2 mt-1"
                    >
                      <option value={15}>15 seconds</option>
                      <option value={30}>30 seconds</option>
                      <option value={60}>60 seconds</option>
                    </select>
                  </div>
                </div>
              )}

              {/* Click URL */}
              <div>
                <Label className="text-gray-300">Click-through URL</Label>
                <Input
                  type="url"
                  value={adCreative.click_url}
                  onChange={(e) => setAdCreative({ ...adCreative, click_url: e.target.value })}
                  className="bg-gray-700 border-gray-600 text-white"
                  placeholder="https://your-website.com"
                />
              </div>

              {uploading && (
                <div className="flex items-center gap-2 text-sky-400">
                  <div className="w-4 h-4 border-2 border-sky-400 border-t-transparent rounded-full animate-spin" />
                  <span>Uploading...</span>
                </div>
              )}
            </>
          )}

          <div className="flex gap-2 pt-4">
            {step === 2 && (
              <Button type="button" variant="outline" onClick={() => setStep(1)} className="border-gray-600">
                Back
              </Button>
            )}
            <Button type="button" variant="outline" onClick={handleClose} className="flex-1 border-gray-600">
              Cancel
            </Button>
            <Button type="submit" className="flex-1 bg-sky-500 hover:bg-sky-600" disabled={loading || uploading}>
              {loading ? 'Creating...' : step === 1 ? 'Next: Upload Creative' : 'Create Campaign'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Main Advertiser Page
const AdvertiserPage = () => {
  const [advertiser, setAdvertiser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const token = getAdvertiserToken();
    const data = getAdvertiserData();
    
    if (token && data) {
      setAdvertiser(data);
    }
    setLoading(false);
  }, []);

  const handleLogin = (data) => {
    setAdvertiser(data);
  };

  const handleLogout = () => {
    clearAdvertiserAuth();
    setAdvertiser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <p className="text-white">Loading...</p>
      </div>
    );
  }

  if (!advertiser) {
    return <AdvertiserAuth onLogin={handleLogin} />;
  }

  return <AdvertiserDashboard advertiser={advertiser} onLogout={handleLogout} />;
};

export default AdvertiserPage;
